import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type Chatter from '@/api/resources/chatters/model'
import type { ChattersListRequest } from '@/api/resources/chatters/model'
import { api } from '@/api'
import type { User } from '@/api/custom/auth/model'
import { useAuthStore } from './auth'
import type { ChatterStatus } from '@/api/resources/chatters/model'

export const useChattersStore = defineStore('chatters', () => {
  const chatters = ref<Chatter[]>([])
  const total = ref(0)
  const stats = ref<Partial<Record<ChatterStatus, number>>>({})
  const curators = ref<User[]>([])
  const loading = ref(false)

  if (!useAuthStore().isCurator) {
    api.getCurators().then((response) => {
      curators.value = response
    })
  }

  const fetch = async (params: ChattersListRequest = {}) => {
    loading.value = true
    try {
      const response = await api.chatters.list(params)
      chatters.value = response.items
      total.value = response.total
      stats.value = response.stats
      return response
    } finally {
      loading.value = false
    }
  }

  const percents = computed(() => {
    const totalCount = Object.values(stats.value).reduce((sum, c) => sum + (c ?? 0), 0)
    if (!totalCount) return {} as Record<ChatterStatus, string>
    return Object.entries(stats.value).reduce(
      (acc, [status, count]) => {
        acc[status as ChatterStatus] = `${Math.round(((count ?? 0) / totalCount) * 100)}%`
        return acc
      },
      {} as Record<ChatterStatus, string>,
    )
  })

  return {
    fetch,
    chatters,
    total,
    curators,
    stats,
    percents,
    loading,
  }
})
